/**
 * useLatestDeployment Hook
 *
 * @module features/releases/hooks/useLatestDeployment
 * @description Fetches the latest deployment for a property.
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';

export type LatestDeployment = {
  id: string;
  commitSha: string;
  commitMessage: string | null;
  deployedBy: string;
  stage: string;
  confirmedAt: string | null;
  triggerSource: string;
  tasksTriggered: number;
  createdAt: string;
};

type LatestDeploymentResponse = {
  ok: boolean;
  deployment: LatestDeployment | null;
};

export function useLatestDeployment(propertyId: string) {
  return useQuery({
    queryKey: ['latest-deployment', propertyId],
    queryFn: async (): Promise<LatestDeployment | null> => {
      const response = await fetch(
        `${API_URL}/api/v1/deployments/latest?propertyId=${encodeURIComponent(propertyId)}&stage=production`,
        { credentials: 'include' }
      );

      if (!response.ok) return null;

      const data: LatestDeploymentResponse = await response.json();
      return data.ok ? data.deployment : null;
    },
    enabled: !!propertyId,
    refetchInterval: 60000,
  });
}
